/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import useStyles from "./styles";
import { Grid } from "@mui/material";
import CardCount from "../../components/CardCount";
import { Group as StudentIcon, Folder, CalendarToday, Person, SwapHoriz } from "@mui/icons-material";
import PageTitle from "../../components/PageTitle";
import WidgetTitle from "./components/WidgetTitle/WidgetTitle";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../components/Table/Table";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";
import { useUserState } from "../../context/UserContext";
import { useWard } from "../../context/GaurdianContext";
import useWindowSize from "../../hooks/useWindow";
import Quote from "inspirational-quotes";
import ClassPeriod from "./components/ClassPeriodCard";
import NoData from "./components/NotFoundCard/NoData";
import NoNotice from "../../../src/assets/images/nofeatureicons/nonotice.svg";
import Modal from "../../components/Modal/index";
import InputGroup from "../../components/FormComponent/InputGroup";
import Button from "../../components/Button";
import NoticeBoardCard from "./components/NoticeBoardCard/NoticeBoardCard";
import SelectGroup from "../../components/FormComponent/SelectGroup";
import { Icon } from "@iconify/react/dist/iconify.js";

const SUBJECT_ENDPOINT = "/api/v1/student/subjects";
const ASSIGNMENT_ENDPOINT = "/api/v1/student/assignment";
const STUDENT_PERIOD = "/api/v1/period";
const TERM_ENDPOINT = "/api/v1/school-plan/terms";

const StudentDashboard = () => {
  const { profile } = useUserState();
  const { selectedWard, wards, changeWard, getCurrentClassId, getCurrentStudentId } = useWard();
  const classes = useStyles();
  const [subjectCount, setSubjectCount] = useState([]);
  const [periodData, setPeriodData] = useState([]);
  const [assignment, setAssignment] = useState([]);
  const [activeTerm, setActiveTerm] = useState([]);
  const [overview, setOverview] = useState([]);
  const [currentNotice, setCurrentNotice] = useState({});
  const [viewNoticeModal, setViewNoticeModal] = useState(false);
  const [quote, setQuote] = useState({ text: "", author: "" });
  const [wardModalOpen, setWardModalOpen] = useState(false);
  const [tempSelectedWard, setTempSelectedWard] = useState(null);

  const width = useWindowSize();
  const mobile = width < 576;
  const navigate = useNavigate();

  /**
   * Determines the calendar view mode based on screen size.
   */
  const renderView = () => {
    if (mobile) {
      return "timeGridDay";
    } else {
      return "timeGridWeek";
    }
  };

  /**
   * Fetches the list of subjects from the API for the selected ward.
   */
  const getSubject = () => {
    const studentId = getCurrentStudentId();
    if (!studentId) return;

    httprequest.get(`${SUBJECT_ENDPOINT}?student_id=${studentId}`)
      .then((res) => {
        setSubjectCount(res?.data?.data || []);
      }).catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
   * Fetches all academic terms from the API and sets the currently active term.
   */
  const getAllTerms = () => {
    httprequest.get(TERM_ENDPOINT).then((res) => {
      setActiveTerm(res?.data?.data.find((active_term) => active_term?.status === "active"));
    }).catch((err) => {
      toast.error(err?.response?.data);
    });
  };

  /**
   * Fetches all assignments from the API for the selected ward.
   */
  const getAssignments = () => {
    const studentId = getCurrentStudentId();
    if (!studentId) return;

    httprequest.get(`${ASSIGNMENT_ENDPOINT}?student_id=${studentId}`)
      .then((res) => {
        setAssignment(res?.data?.data || []);
      }).catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
   * Fetches all periods for the selected ward's class.
   */
  const getAllPeriods = () => {
    const classId = getCurrentClassId();
    if (!classId) return;

    httprequest
      .get(`${STUDENT_PERIOD}?class_id=${classId}`)
      .then((res) => {
        setPeriodData(res?.data || {});
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
   * Opens the notice modal and sets the currently selected notice to be viewed.
   */
  const handleViewNoticeModal = (notice) => {
    setCurrentNotice(notice);
    setViewNoticeModal(!viewNoticeModal);
  };

  /**
   * Closes the view notice modal by toggling its visibility.
   */
  const handleCloseViewNoticeModal = () => {
    setViewNoticeModal(!viewNoticeModal);
  };

  const refreshWardData = () => {
    // Refresh all data that's ward-specific
    getSubject();
    getAssignments();
    getAllPeriods();
  };
  const handleTempWardChange = (wardId) => {
    const ward = wards.find(w => w.id === wardId);
    setTempSelectedWard(ward);
  };

  const handleConfirmWardSelection = () => {
    if (tempSelectedWard) {
      changeWard(tempSelectedWard.id);
      setWardModalOpen(false);
      toast.success(`Now viewing ${tempSelectedWard.first_name}'s dashboard`);
      setTempSelectedWard(null);
    } else {
      toast.warning("Please select a ward first");
    }
  };

  const handleCloseWardModal = () => {
    setWardModalOpen(false);
    setTempSelectedWard(null);
  };

  const handleOpenWardModal = () => {
    setWardModalOpen(true);
  };

  // Calendar event generation
  const startDate = new Date(activeTerm?.start_date);
  const EndDate = new Date(activeTerm?.end_date);

  const generateEvents = () => {
    const allEvents = [];
    const currentDate = new Date(startDate);

    while (currentDate <= EndDate) {
      const weekDay = currentDate
        .toLocaleDateString("en-US", { weekday: "short" })
        .toLowerCase();

      periodData?.data?.days?.forEach((day) => {
        if (weekDay === day?.day) {
          day?.periods?.forEach((periodItem) => {
            const event = {
              type: periodItem?.type,
              title: periodItem?.subject?.name || periodItem?.title,
              start: `${currentDate.toISOString().split("T")[0]}T${periodItem?.start_time}`,
              end: `${currentDate.toISOString().split("T")[0]}T${periodItem?.end_time}`,
              teacher: periodItem?.teachers?.[0]?.name,
              subject_name: periodItem?.subject?.name,
              subject_id: periodItem?.subject?.id,
              day: day?.day,
              class: periodData?.data?.class?.name,
              class_id: periodData?.data?.class?.id,
              term: periodData?.data?.term?.name,
              term_id: periodData?.data?.term?.id,
              period_id: periodItem?.id,
              className: "general-events"
            };
            allEvents.push(event);
          });
        }
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allEvents;
  };

  const events = generateEvents();

  const renderEventContent = (eventInfo) => {
    return (
      <>
        <p className="no-margin-block padding-inline-10">{eventInfo.event?.title}</p>
        <p className="no-margin-block padding-inline-10">{eventInfo.event?.extendedProps?.teacher}</p>
      </>
    );
  };

  useEffect(() => {
    if (selectedWard) {
      refreshWardData();
    }
  }, [selectedWard]);

  useEffect(() => {
    getAllTerms();
    const fetchedQuote = Quote.getQuote();
    setQuote(fetchedQuote);
  }, []);

  const assignmentsColumns = [
    {
      name: "Subject",
      selector: row => row?.subject?.name
    },
    {
      name: "Instruction",
      selector: row => row?.instruction || "--------"
    },
    {
      name: "Questions",
      selector: row => row?.questions?.length
    },
    {
      name: "Total Score",
      selector: row => row?.total_grade
    },
    {
      name: "Submission Date",
      selector: row => row?.submission_date?.substring(0, 10)
    }
  ];

  if (!selectedWard) {
  // Define WardSelectorIcon component outside the return
    const WardSelectorIcon = () => {
      if (!wards || wards.length === 0) return null;

      return (
        <div className={classes.wardSelectorIcon}>
          <Button
            variant="primaryoutline"
            buttonSize="small"
            color="btnfontprimary"
            buttonName="Select Ward"
            onClick={handleOpenWardModal}
            icon={<Icon icon="fluent-mdl2:switch-user" width="16" height="16" />}
          />
        </div>
      );
    };

    return (
      <Layout>
        <PageTitle
          title={`Hello ${profile?.last_name} ${profile?.first_name},`}
          subtitle={
            <div className={classes.quoteContainer}>
              <h6 className={classes.quoteText}>{quote.text}</h6>
              <h5 className={classes.quoteAuthor}> - {quote.author}</h5>
            </div>
          }
          button={<WardSelectorIcon />}
        />
        <NoData
          img={NoNotice}
          title="No Ward Selected"
          subtitle="Please select a ward to view their dashboard"
        />
      </Layout>
    );
  }

  return (
    <Layout
      children={
        <>
          <PageTitle
            title={`Hello ${profile?.last_name} ${profile?.first_name},`}
            button={
              profile?.branding?.website_url ? (
                <Button
                  variant="primary"
                  buttonSize="fluid"
                  color="btndefault"
                  onClick={() => window.open(profile?.branding?.website_url, "_blank")}
                  buttonName="Visit school website"
                />
              ) : null
            }
          />

          {selectedWard && (
            <div className={classes.wards}>
              <p className={classes.wardText}>Ward</p>
              <div className={classes.wardBody}>
                <div className={classes.wardInfo}>
                  <h3 className={classes.wardName}>
                    Name: {selectedWard.first_name} {selectedWard.last_name}
                  </h3>
                  <p className={classes.wardClass}>
                    <span className={classes.schoolName}>{profile?.school?.name}</span> || {selectedWard.class_name}
                  </p>
                </div>
                <span onClick={handleOpenWardModal} className={classes.iconSpan}>
                  <Icon icon="fluent-mdl2:switch-user" width="20" height="20" />
                </span>
              </div>

            </div>
          )}

          <Grid container spacing={{ xs: 1, sm: 2, md: 3, lg: 4 }}>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Subjects"
                count={subjectCount.length}
                icon={<Folder />}
              />
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CardCount
                title="Pending Assignments"
                count={assignment.filter(a => a.status === "pending").length}
                icon={<StudentIcon />}
              />
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CardCount
                title="Completed Assignments"
                count={assignment.filter(a => a.status === "completed" || a.student_score > 0).length}
                icon={<StudentIcon />}
              />
            </Grid>
          </Grid>

          <WidgetTitle
            title="My Assignments"
            link={<Link style={{ fontSize: "14px", color: "black", fontWeight: "400" }} className={classes.widgetlink} to="/assesments/assignments">View all assignments</Link>}
          />
          <Table data={assignment} columns={assignmentsColumns} />

          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <WidgetTitle
                title="Today's Classes"
                subtitle={`View ${selectedWard.first_name} ${selectedWard.last_name} schedule for the day`}
              />
              <div className={classes.period}>
                <div className={classes.static}>
                  <h3 style={{ fontSize: "14px" }} onClick={() => navigate("/class-activities/classes/class-calendar")}>View all</h3>
                  <hr />
                </div>
                <div className={classes.periodclasses}>
                  <div className={classes.preriodflex}>
                    {periodData?.data?.days && periodData.data.days.length > 0 ? (
                      (() => {
                        const today = new Date().toLocaleDateString("en-US", { weekday: "short" }).toLowerCase();
                        const todayPeriods = periodData.data.days.find(day => day.day === today)?.periods || [];

                        return todayPeriods.length > 0 ? (
                          todayPeriods.map((periodItem, index) => (
                            <div key={`${periodItem.id}-${index}`} className={classes.perioditem}>
                              <ClassPeriod
                                start_time={periodItem?.start_time}
                                end_time={periodItem?.end_time}
                                period_subject={periodItem?.subject?.name || periodItem?.title || "Activity"}
                                period_class={periodData?.data?.class?.name}
                              />
                            </div>
                          ))
                        ) : (
                          <div className={classes.notfound}>
                            <NoData
                              img={NoNotice}
                              title="No Classes Today"
                              subtitle="You have no scheduled classes for today"
                            />
                          </div>
                        );
                      })()
                    ) : (
                      <div className={classes.notfound}>
                        <NoData
                          img={NoNotice}
                          title="No Schedule Available"
                          subtitle="No class schedule has been set up yet"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>

          {/* Ward Selection Modal */}
          <Modal
            title="Select Ward"
            subtitle="Choose which ward's dashboard to view"
            isopen={wardModalOpen}
            closeModal={handleCloseWardModal}
            modalContent={
              <div className={classes.wardModalContent}>
                <p>Select a ward to view their dashboard:</p>
                <SelectGroup
                  label=""
                  children={
                    <select
                      value={tempSelectedWard?.id || selectedWard?.id || ""}
                      onChange={(e) => {
                        if (e.target.value) {
                          handleTempWardChange(e.target.value);
                        }
                      }}
                      className={classes.wardSelect}
                    >
                      <option value="">Select a ward</option>
                      {wards.map((ward) => (
                        <option key={ward.id} value={ward.id}>
                          {ward.first_name} {ward.last_name} - {ward.class_name}
                          {selectedWard?.id === ward.id ? " (Current)" : ""}
                        </option>
                      ))}
                    </select>
                  }
                />
                {tempSelectedWard && (
                  <div className={classes.selectedWardPreview}>
                    <p><strong>Selected:</strong> {tempSelectedWard.first_name} {tempSelectedWard.last_name} - {tempSelectedWard.class_name}</p>
                  </div>
                )}
              </div>
            }
            modalFooter={
              <div className="flex gap-10">
                <Button
                  variant="primaryoutline"
                  buttonName="Cancel"
                  buttonSize="full"
                  color="btnfontprimary"
                  onClick={handleCloseWardModal}
                />
                <Button
                  variant="primary"
                  buttonName="Confirm Selection"
                  buttonSize="full"
                  color="btndefault"
                  onClick={handleConfirmWardSelection}
                  disabled={!tempSelectedWard}
                />
              </div>
            }
          />

          {/* Notice View Modal */}
          <Modal
            title="View a Notice"
            subtitle="View a notice set from your school"
            modalContent={
              <>
                <Grid container spacing={2} className="input-padding">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    {currentNotice?.image?.file_url && (
                      <div
                        className={classes.viewBanner}
                        style={{
                          backgroundImage: `url(${currentNotice?.image?.file_url})`,
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat"
                        }}
                      >
                      </div>
                    )}
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Title"
                      required
                      disabled="disabled"
                      margin="normal"
                      placeHolder="Add Title of Document"
                      inputType="text"
                      inputName="title"
                      defaultValue={currentNotice?.title}
                    />
                  </Grid>
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="End Date"
                      margin="normal"
                      disabled="disabled"
                      inputType="text"
                      required
                      value={currentNotice?.end_date}
                    />
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Description"
                      margin="normal"
                      required
                      disabled="disabled"
                      placeHolder="Notice Description"
                      inputType="text"
                      inputName="description"
                      defaultValue={currentNotice?.description}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Priority"
                      margin="normal"
                      required
                      disabled="disabled"
                      placeHolder="Notice priority"
                      inputType="text"
                      inputName="priority"
                      defaultValue={currentNotice?.priority}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <InputGroup
                      label="Status"
                      margin="normal"
                      required
                      disabled="disabled"
                      placeHolder="Notice status"
                      inputType="text"
                      inputName="Status"
                      defaultValue={currentNotice?.status}
                    />
                  </Grid>
                </Grid>
              </>
            }
            modalFooter={
              <>
                <div className="flex gap-10">
                  <Button
                    variant="primaryoutline"
                    buttonName="Cancel"
                    buttonSize="full"
                    color="btnfontprimary"
                    onClick={handleCloseViewNoticeModal}
                  />
                </div>
              </>
            }
            isopen={viewNoticeModal}
            closeModal={handleCloseViewNoticeModal}
          />

          {/* <ToastContainer /> */}
        </>
      }
    />
  );
};

export default StudentDashboard;
